/**
 * Adapter `RequesterSupportApi` per il lato richiedente.
 *
 * - `associate`: `supportService.ts`, token OTP dell'associato.
 * - `guest`: `guestSupportService.ts`, token HMAC del link email catturato in
 *   closure (non viene mai persistito, resta solo in memoria come nella pagina).
 */

import * as associate from '../supportService';
import * as guest from '../guestSupportService';
import type { RequesterSupportApi, RequesterTicketDetail } from '../supportApi';

/** Adapter per l'associato autenticato (`/api/support/*`). */
export const associateSupportApi: RequesterSupportApi = {
  kind: 'associate',
  getTicket: (id: string): Promise<RequesterTicketDetail> => associate.getTicket(id),
  reply: (id: string, body: string, files?: File[]) => associate.sendMessage(id, body, files),
  rate: (id: string, stars: number, comment: string) =>
    associate.rateTicket(id, stars, comment || undefined),
  reopen: (id: string) => associate.reopenTicket(id),
  openAttachment: (id: string, storedName: string, displayName?: string) =>
    associate.openAttachment(id, storedName, displayName),
};

/** Adapter per l'ospite (`/api/guest-tickets/*`): il token vive solo qui. */
export const createGuestSupportApi = (token: string): RequesterSupportApi => ({
  kind: 'guest',
  getTicket: (id: string): Promise<RequesterTicketDetail> => guest.getGuestTicket(id, token),
  reply: (id: string, body: string, files?: File[]) => guest.replyGuest(id, token, body, files),
  rate: (id: string, stars: number, comment: string) =>
    guest.rateGuest(id, token, stars, comment || undefined),
  reopen: (id: string) => guest.reopenGuest(id, token),
  openAttachment: (id: string, storedName: string, displayName?: string) =>
    guest.openGuestAttachment(id, token, storedName, displayName),
});

/** Factory unica: `kind` sceglie il canale, `token` serve solo agli ospiti. */
export const createRequesterSupportApi = (
  kind: 'associate' | 'guest',
  token?: string
): RequesterSupportApi =>
  kind === 'guest' ? createGuestSupportApi(token || '') : associateSupportApi;

export default createRequesterSupportApi;
